import React, { useEffect, useRef, useState } from "react";
import { FormControl, FormLabel, Input } from "@mui/joy";
import { Accordion, Button, Form } from "react-bootstrap";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  create_faq_question,
  get_all_faq_questions,
  upload_video_content,
  upload_cms_photo,
} from "../../reduxData/cms/cmsAction";
import CollectionPickerModal from "../../Modals/CollectionPickerModal";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";

const HomeVideoContent = ({ faqItems }) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const videoRef = useRef();
  const imageRef = useRef();

  const [isPermission, setIsPermission] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [collections, setCollections] = useState([]); // [{ name, slug }]
  const [videoData, setVideoData] = useState({
    title: "",
    description: "",
    videoUrl: "",
    backgroundImage: "",
    backgroundColor: "#000000",
    opacity: 0.7,
    status: false,
    id: null,
    buttonType: "view_auction",
    auctionSlug: "",
    collectionSlug: "",
  });

  useEffect(() => {
    setIsPermission(
      (user?.role === "staff" && user?.permission?.p_staff > 1) ||
        user?.role !== "staff",
    );
  }, [user]);

  useEffect(() => {
    const fetchContent = async () => {
      await get_all_faq_questions(
        localStorage.getItem("token"),
        "home_video_content",
        dispatch,
      );
    };
    fetchContent();
  }, [dispatch]);

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    const videoItem = faqItems?.find(
      (item) => item.type === "home_video_content",
    );

    if (videoItem) {
      setVideoData({
        title: videoItem?.content?.title || "",
        description: videoItem?.content?.description || "",
        videoUrl: videoItem?.content?.videoUrl || "",
        backgroundImage: videoItem?.content?.backgroundImage || "",
        backgroundColor: videoItem?.content?.backgroundColor || "#000000",
        opacity: videoItem?.content?.opacity ?? 0.7,
        status: videoItem?.status || false,
        id: videoItem?._id || null,
        buttonType: videoItem?.content?.buttonType || "view_auction",
        auctionSlug: videoItem?.content?.auctionSlug || "",
        collectionSlug: videoItem?.content?.collectionSlug || "",
      });
    }
  }, [faqItems]);

  const fetchCollections = async () => {
    try {
      const res = await apiClient.get("/collections?limit=200");
      if (res.data?.status) {
        const active = (res.data.data || [])
          .filter((c) => c.isActive !== false)
          .map((c) => ({ name: c.name, slug: c.slug }));
        setCollections(active);
      }
    } catch (e) {
      console.error("Failed to fetch collections", e);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const res = await upload_video_content(
      file,
      localStorage.getItem("token"),
      dispatch,
    );

    if (res && res.status) {
      setVideoData((prev) => ({ ...prev, videoUrl: res.path[0] }));
    }
    setIsUploading(false);
    e.target.value = "";
  };

  const handleImageUpload = async (e) => {
    const file = [e.target.files[0]];
    if (!file[0]) return;

    setIsUploading(true);
    const photoData = await upload_cms_photo(
      file,
      localStorage.getItem("token"),
      dispatch,
    );

    if (photoData?.data?.photos?.[0]) {
      setVideoData((prev) => ({
        ...prev,
        backgroundImage: photoData.data.photos[0],
      }));
    }
    setIsUploading(false);
  };

  const handleStatus = (e) => {
    setVideoData({ ...videoData, status: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoData.title || !videoData.videoUrl) {
      toast.error("Both a Title and a Video are required.");
      return;
    }

    if (
      videoData.buttonType === "view_collection" &&
      (!videoData.collectionSlug || videoData.collectionSlug === "__new__")
    ) {
      toast.error("Please select or create a collection first.");
      return;
    }

    const homedata = {
      name: "home_video_content",
      type: "home_video_content",
      status: videoData.status,
      content: {
        title: videoData.title,
        description: videoData.description,
        videoUrl: videoData.videoUrl,
        backgroundImage: videoData.backgroundImage,
        backgroundColor: videoData.backgroundColor,
        opacity: videoData.opacity,
        buttonType: videoData.buttonType,
        auctionSlug: videoData.auctionSlug,
        collectionSlug: videoData.collectionSlug,
      },
    };

    await create_faq_question(
      localStorage.getItem("token"),
      homedata,
      videoData.id,
      dispatch,
    );

    await get_all_faq_questions(
      localStorage.getItem("token"),
      "home_video_content",
      dispatch,
    );
  };

  const handleRefetchCollections = () => fetchCollections();

  const handleCreateCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) return;

    const existing = collections.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) {
      setVideoData({ ...videoData, collectionSlug: existing.slug });
      setNewCollectionName("");
      return;
    }

    setCreatingCollection(true);
    try {
      const res = await apiClient.post("/collections", { name });
      if (res.data?.status) {
        const created = res.data.data;
        toast.success(`Collection "${created.name}" created`);
        setVideoData({ ...videoData, collectionSlug: created.slug });
        setNewCollectionName("");
        await fetchCollections();
      } else {
        toast.error("Failed to create collection");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to create collection");
    } finally {
      setCreatingCollection(false);
    }
  };

  const selectedSlugIsKnown =
    videoData.collectionSlug &&
    videoData.collectionSlug !== "__new__" &&
    collections.some((c) => c.slug === videoData.collectionSlug);

  const selectValue =
    videoData.collectionSlug === "__new__"
      ? "__new__"
      : selectedSlugIsKnown
        ? videoData.collectionSlug
        : videoData.collectionSlug
          ? "__new__" 
          : "";

  return (
    <div className="container mt-4">
      <Accordion defaultActiveKey={["0"]} alwaysOpen>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <h4 className="mb-0 fw-bold">Now Playing Video Section</h4>
          </Accordion.Header>
          <Accordion.Body>
            <Form onSubmit={handleSubmit}>
              {/* Title */}
              <FormControl className="mt-2 mb-3">
                <FormLabel className="fw-600">
                  Video Title (e.g. Sony Legacy Collection)
                </FormLabel>
                <Input
                  type="text"
                  value={videoData.title}
                  disabled={!isPermission}
                  onChange={(e) =>
                    setVideoData({ ...videoData, title: e.target.value })
                  }
                />
              </FormControl>

              {/* Description */}
              <FormControl className="mt-2 mb-3">
                <FormLabel className="fw-600">Short Description</FormLabel>
                <textarea
                  className="form-control"
                  rows="3"
                  value={videoData.description}
                  disabled={!isPermission}
                  style={{
                    resize: "none",
                    border: "2px solid #e0e0e0",
                    borderRadius: "6px",
                  }}
                  onChange={(e) =>
                    setVideoData({ ...videoData, description: e.target.value })
                  }
                />
              </FormControl>

              {/* Video Upload */}
              <FormControl className="mt-2 mb-4">
                <FormLabel className="fw-600">Promo Video (MP4)</FormLabel>
                {videoData.videoUrl && (
                  <div className="position-relative pb-3">
                    <video
                      src={videoData.videoUrl}
                      controls
                      style={{
                        height: "250px",
                        width: "100%",
                        objectFit: "contain",
                        backgroundColor: "#000",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  className="d-none"
                  accept="video/mp4, video/quicktime"
                  ref={videoRef}
                  disabled={!isPermission || isUploading}
                  onChange={handleVideoUpload}
                />
                <Button
                  className="btn upload-button"
                  disabled={!isPermission || isUploading}
                  onClick={() => videoRef.current.click()}
                >
                  {isUploading ? "Uploading..." : "Upload Video"}
                </Button>
              </FormControl>

              {/* Background Image */}
              <FormControl className="mt-2 mb-3">
                <FormLabel className="fw-600">Background Image</FormLabel>
                {videoData.backgroundImage && (
                  <img
                    src={videoData.backgroundImage}
                    alt="Background Preview"
                    style={{
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "10px",
                    }}
                  />
                )}
                <input
                  type="file"
                  className="d-none"
                  accept="image/*"
                  ref={imageRef}
                  disabled={!isPermission || isUploading}
                  onChange={handleImageUpload}
                />
                <Button
                  className="btn btn-secondary"
                  disabled={!isPermission || isUploading}
                  onClick={() => imageRef.current.click()}
                >
                  Upload Background Image
                </Button>
              </FormControl>

              {/* Tint Controls */}
              <div className="d-flex gap-4 mb-4">
                <FormControl>
                  <FormLabel className="fw-600">Tint Color</FormLabel>
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={videoData.backgroundColor}
                    disabled={!isPermission}
                    onChange={(e) =>
                      setVideoData({
                        ...videoData,
                        backgroundColor: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl className="flex-grow-1">
                  <FormLabel className="fw-600">
                    Tint Opacity: {videoData.opacity}
                  </FormLabel>
                  <input
                    className="form-range"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={videoData.opacity}
                    disabled={!isPermission}
                    onChange={(e) =>
                      setVideoData({
                        ...videoData,
                        opacity: parseFloat(e.target.value),
                      })
                    }
                  />
                </FormControl>
              </div>

              {/* CTA Button Type */}
              <FormControl className="mt-2 mb-3">
                <FormLabel className="fw-600">CTA Button Action</FormLabel>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="btn-view-auction"
                    label="View Auction"
                    value="view_auction"
                    checked={videoData.buttonType === "view_auction"}
                    disabled={!isPermission}
                    onChange={(e) =>
                      setVideoData({ ...videoData, buttonType: e.target.value })
                    }
                  />
                  <Form.Check
                    type="radio"
                    id="btn-view-collection"
                    label="View Collection"
                    value="view_collection"
                    checked={videoData.buttonType === "view_collection"}
                    disabled={!isPermission}
                    onChange={(e) =>
                      setVideoData({ ...videoData, buttonType: e.target.value })
                    }
                  />
                </div>
              </FormControl>

              {/* View Auction — slug input */}
              {videoData.buttonType === "view_auction" && (
                <FormControl className="mt-2 mb-4">
                  <FormLabel className="fw-600">
                    Auction Slug
                    <span
                      className="text-muted fw-normal ms-2"
                      style={{ fontSize: "0.8rem" }}
                    >
                      e.g. <code>1990s-banpresto-niko-niko-pikachu-ride</code> →
                      links to <code>/auction/...</code>
                    </span>
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder="e.g. 1990s-banpresto-niko-niko-pikachu-ride"
                    value={videoData.auctionSlug}
                    disabled={!isPermission}
                    onChange={(e) =>
                      setVideoData({
                        ...videoData,
                        auctionSlug: e.target.value,
                      })
                    }
                  />
                </FormControl>
              )}

              {videoData.buttonType === "view_collection" && (
                <>
                  <FormControl className="mt-2 mb-2">
                    <FormLabel className="fw-600">
                      Collection{" "}
                      <a
                        href="https://admin.insertbid.com/collections"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-normal"
                        style={{ fontSize: "0.8rem", color: "#0d6efd", textDecoration: "underline" }}
                      >
                        Go to Collection Manager
                      </a>
                    </FormLabel>
                    <select
                      className="form-select mb-2"
                      value={selectValue}
                      disabled={!isPermission}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setNewCollectionName("");
                          setVideoData({
                            ...videoData,
                            collectionSlug: "__new__",
                          });
                        } else {
                          setNewCollectionName("");
                          setVideoData({
                            ...videoData,
                            collectionSlug: e.target.value,
                          });
                        }
                      }}
                    >
                      <option value="">
                        — Select an existing collection —
                      </option>
                      {collections.map((col) => (
                        <option key={col.slug} value={col.slug}>
                          {col.name}
                        </option>
                      ))}
                      {/* <option value="__new__">+ Create new collection…</option> */}
                    </select>

                    {videoData.collectionSlug === "__new__" && (
                      <div className="d-flex align-items-start gap-2 mt-2">
                        <Input
                          type="text"
                          placeholder="e.g. Sony Legacy Collection"
                          autoFocus
                          value={newCollectionName}
                          disabled={!isPermission || creatingCollection}
                          onChange={(e) => setNewCollectionName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCreateCollection();
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          className="btn common-button"
                          disabled={
                            !isPermission ||
                            creatingCollection ||
                            !newCollectionName.trim()
                          }
                          onClick={handleCreateCollection}
                        >
                          {creatingCollection ? "Creating..." : "Create"}
                        </Button>
                      </div>
                    )}

                    {selectedSlugIsKnown && (
                      <small className="text-muted mt-1 d-block">
                        Links to{" "}
                        <code>/collection/{videoData.collectionSlug}</code>
                      </small>
                    )}
                  </FormControl>

                  {/* <div className="mb-4">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!isPermission || !selectedSlugIsKnown}
                      onClick={() => setShowPicker(true)}
                      title={
                        !selectedSlugIsKnown
                          ? "Select or create a collection first"
                          : `Add auctions to "${
                              collections.find(
                                (c) => c.slug === videoData.collectionSlug,
                              )?.name
                            }"`
                      }
                    >
                      + Add auctions to this collection
                    </Button>
                    {!selectedSlugIsKnown && (
                      <small className="text-muted ms-2">
                        {videoData.collectionSlug === "__new__"
                          ? "Create the collection above first"
                          : "Select or create a collection first"}
                      </small>
                    )}
                  </div> */}
                </>
              )}

              {/* Status */}
              <FormControl className="mt-1 mb-4">
                <FormLabel className="fw-600">
                  Status (Show on Homepage)
                </FormLabel>
                <div className="form-check form-switch switch-large">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={videoData.status}
                    disabled={!isPermission}
                    onChange={handleStatus}
                  />
                </div>
              </FormControl>

              <Button
                type="submit"
                className="btn common-button"
                disabled={!isPermission || isUploading}
              >
                Save Video Section
              </Button>
            </Form>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* Modal lives outside the form to avoid nesting issues */}
      <CollectionPickerModal
        show={showPicker}
        onClose={() => setShowPicker(false)}
        collectionName={videoData.collectionSlug}
        onSaved={handleRefetchCollections}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    faqItems: state.cms.faqItems,
  };
};

export default connect(mapStateToProps)(HomeVideoContent);
